/**
 * Checklist
 *
 * A component for displaying interactive checklists in chat messages
 *
 * Primary responsibilities:
 * - Display a structured list of tasks with categories
 * - Allow marking items as complete/incomplete
 * - Support priority indicators
 * - Track progress by category and overall
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import {
  IconList,
  IconCheck,
  IconArrowsShuffle,
  IconFilter,
  IconChevronDown,
  IconChevronRight
} from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'

type Priority = 'low' | 'medium' | 'high'

interface ChecklistItem {
  id: string
  text: string
  category?: string
  priority?: Priority
}

interface ChecklistProps {
  title?: string
  items: ChecklistItem[]
  className?: string
}

export const Checklist: React.FC<ChecklistProps> = ({ title = 'Checklist', items, className }) => {
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set())

  // Extract unique categories and organize items
  const categories = [...new Set(items.map((item) => item.category || 'Uncategorized'))]

  // Auto-expand if there's only one category for better UX
  const initialExpandedCategories =
    categories.length === 1 ? new Set<string>(categories) : new Set<string>()
  const [expandedCategories, setExpandedCategories] =
    useState<Set<string>>(initialExpandedCategories)
  const [filterPriority, setFilterPriority] = useState<Priority | null>(null)

  const itemsByCategory: Record<string, ChecklistItem[]> = {}
  categories.forEach((category) => {
    itemsByCategory[category] = items.filter(
      (item) => (item.category || 'Uncategorized') === category
    )
  })

  const toggleItem = (id: string) => {
    const newCompletedItems = new Set(completedItems)
    if (newCompletedItems.has(id)) {
      newCompletedItems.delete(id)
    } else {
      newCompletedItems.add(id)
    }
    setCompletedItems(newCompletedItems)
  }

  const toggleCategory = (category: string) => {
    const newExpandedCategories = new Set(expandedCategories)
    if (newExpandedCategories.has(category)) {
      newExpandedCategories.delete(category)
    } else {
      newExpandedCategories.add(category)
    }
    setExpandedCategories(newExpandedCategories)
  }

  const resetAll = () => {
    setCompletedItems(new Set())
  }

  const completeAll = () => {
    const filteredItems = filterPriority
      ? items.filter((item) => item.priority === filterPriority)
      : items

    const allItemIds = new Set(filteredItems.map((item) => item.id))
    setCompletedItems(allItemIds)
  }

  const toggleFilter = (priority: Priority) => {
    setFilterPriority(filterPriority === priority ? null : priority)
  }

  // Calculate progress
  const filteredItems = filterPriority
    ? items.filter((item) => item.priority === filterPriority)
    : items

  const filteredIds = new Set(filteredItems.map((item) => item.id))
  const relevantCompletedItems = new Set([...completedItems].filter((id) => filteredIds.has(id)))

  const totalCount = filteredItems.length
  const completedCount = relevantCompletedItems.size
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  // Helper for priority styling
  const getPriorityStyles = (priority: Priority | undefined) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 dark:text-red-400'
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'low':
        return 'text-green-600 dark:text-green-400'
      default:
        return ''
    }
  }

  const getPriorityLabel = (priority: Priority | undefined) => {
    switch (priority) {
      case 'high':
        return 'High'
      case 'medium':
        return 'Medium'
      case 'low':
        return 'Low'
      default:
        return ''
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }}
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconList size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
          <span className="text-muted-foreground text-xs">
            ({completedCount}/{totalCount})
          </span>
        </div>
      </div>

      <div className="p-2">
        {/* Progress Bar */}
        {progress > 0 && (
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
            className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
          >
            <motion.div
              className="h-full rounded-full bg-blue-500 transition-all"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
            />
          </motion.div>
        )}

        {/* Priority filters */}
        <div className="mb-3 flex flex-wrap items-center justify-start gap-2">
          <span className="flex items-center gap-1 text-xs font-medium">
            <IconFilter size={12} />
            Filter:
          </span>

          <Button
            variant={filterPriority === 'high' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('high')}
            className="h-6 border-red-200 bg-red-50 px-2 text-xs text-red-600 hover:bg-red-100 dark:border-red-900/30 dark:bg-red-950/20 dark:text-red-400 dark:hover:bg-red-900/40"
          >
            High
          </Button>

          <Button
            variant={filterPriority === 'medium' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('medium')}
            className="h-6 border-yellow-200 bg-yellow-50 px-2 text-xs text-yellow-600 hover:bg-yellow-100 dark:border-yellow-900/30 dark:bg-yellow-950/20 dark:text-yellow-400 dark:hover:bg-yellow-900/40"
          >
            Medium
          </Button>

          <Button
            variant={filterPriority === 'low' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('low')}
            className="h-6 border-green-200 bg-green-50 px-2 text-xs text-green-600 hover:bg-green-100 dark:border-green-900/30 dark:bg-green-950/20 dark:text-green-400 dark:hover:bg-green-900/40"
          >
            Low
          </Button>
        </div>

        {/* Items by category */}
        <div className="space-y-3">
          {categories.map((category, categoryIndex) => {
            const categoryItems = itemsByCategory[category].filter(
              (item) => !filterPriority || item.priority === filterPriority
            )

            if (categoryItems.length === 0) return null

            const categoryCompletedCount = categoryItems.filter((item) =>
              completedItems.has(item.id)
            ).length
            const categoryProgress = Math.round(
              (categoryCompletedCount / categoryItems.length) * 100
            )
            const isExpanded = expandedCategories.has(category)

            return (
              <motion.div
                key={category}
                className="overflow-hidden rounded-md border"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.1 + categoryIndex * 0.1,
                  duration: 0.4,
                  ease: 'easeOut'
                }}
                whileHover={{
                  scale: 1.01,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.div
                  className="bg-muted/30 flex cursor-pointer items-center justify-between p-2"
                  onClick={() => toggleCategory(category)}
                  whileHover={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{ rotate: isExpanded ? 0 : -90 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                    >
                      <IconChevronDown size={14} className="text-muted-foreground" />
                    </motion.div>
                    <h4 className="text-sm font-medium">{category}</h4>
                    <span className="text-muted-foreground text-xs">
                      ({categoryCompletedCount}/{categoryItems.length})
                    </span>
                  </div>

                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <motion.div
                      className="h-full rounded-full bg-blue-500 transition-all"
                      initial={{ width: 0 }}
                      animate={{ width: `${categoryProgress}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  </div>
                </motion.div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.ul
                      className="space-y-1 p-2"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      {categoryItems.map((item, itemIndex) => (
                        <motion.li
                          key={item.id}
                          className="hover:bg-accent/50 flex items-start gap-2 rounded-md p-1.5 transition-colors"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: itemIndex * 0.05,
                            duration: 0.3,
                            ease: 'easeOut'
                          }}
                          whileHover={{
                            scale: 1.02,
                            backgroundColor: 'rgba(0,0,0,0.02)',
                            transition: { duration: 0.15 }
                          }}
                        >
                          <Checkbox
                            id={`checklist-item-${item.id}`}
                            checked={completedItems.has(item.id)}
                            onCheckedChange={() => toggleItem(item.id)}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <motion.label
                              htmlFor={`checklist-item-${item.id}`}
                              className={cn(
                                'flex-1 cursor-pointer text-sm leading-relaxed',
                                completedItems.has(item.id) && 'text-muted-foreground line-through'
                              )}
                              animate={{
                                opacity: completedItems.has(item.id) ? 0.6 : 1,
                                scale: completedItems.has(item.id) ? 0.98 : 1
                              }}
                              transition={{ duration: 0.2 }}
                            >
                              {item.text}
                            </motion.label>

                            {item.priority && (
                              <motion.span
                                className={cn(
                                  'ml-2 text-xs font-medium',
                                  getPriorityStyles(item.priority)
                                )}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.1, duration: 0.2 }}
                              >
                                {getPriorityLabel(item.priority)}
                              </motion.span>
                            )}
                          </div>
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>

        {/* Actions */}
        <motion.div
          className="mt-4 flex items-center gap-2 border-t pt-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button variant="outline" size="sm" onClick={resetAll} className="text-xs">
              <IconArrowsShuffle size={14} className="mr-1" />
              Reset
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button variant="outline" size="sm" onClick={completeAll} className="text-xs">
              <IconCheck size={14} className="mr-1" />
              Complete All
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  )
}
